import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import { Button } from '../components/ui/Button'
import { PasswordField } from '../components/ui/PasswordField'

const Schema = z
  .object({
    currentPassword: z.string().min(1, 'Introdu parola curentă'),
    newPassword: z
      .string()
      .min(8, 'Parola nouă trebuie să aibă cel puțin 8 caractere')
      .max(128)
      .refine((v) => /[A-Z]/.test(v), 'Cel puțin o literă mare')
      .refine((v) => /[a-z]/.test(v), 'Cel puțin o literă mică')
      .refine((v) => /\d/.test(v), 'Cel puțin o cifră'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Parolele nu coincid',
  })
  .refine((d) => d.currentPassword !== d.newPassword, {
    path: ['newPassword'],
    message: 'Parola nouă trebuie să fie diferită de cea curentă',
  })

type Input = z.infer<typeof Schema>

export function ChangePasswordPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const permissions = useAuthStore((s) => s.permissions)
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  if (!user) return null

  const forced = user.mustChangePassword

  const onSubmit = async (data: Input) => {
    setSubmitting(true)
    try {
      await api.post('/auth/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      })
      setUser({ ...user, mustChangePassword: false }, permissions)
      toast.success('Parola a fost schimbată.')
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la schimbarea parolei'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-b4l-bg">
      <div className="w-full max-w-sm rounded-lg border border-b4l-border bg-white p-8 shadow-sm">
        <h1 className="mb-1 text-xl font-semibold text-b4l-green-dark">Schimbare parolă</h1>
        <p className="mb-6 text-sm text-b4l-text-muted">
          {forced
            ? 'La prima autentificare trebuie să îți setezi o parolă nouă.'
            : 'Schimbă parola contului.'}
        </p>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <PasswordField
            label="Parola curentă"
            autoComplete="current-password"
            error={form.formState.errors.currentPassword?.message}
            {...form.register('currentPassword')}
          />
          <PasswordField
            label="Parola nouă"
            autoComplete="new-password"
            error={form.formState.errors.newPassword?.message}
            {...form.register('newPassword')}
          />
          <PasswordField
            label="Confirmă parola nouă"
            autoComplete="new-password"
            error={form.formState.errors.confirmPassword?.message}
            {...form.register('confirmPassword')}
          />
          <Button type="submit" loading={submitting} className="w-full">
            Schimbă parola
          </Button>
        </form>
      </div>
    </div>
  )
}
